import { ReactNode } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface MetricItemProps {
  value: ReactNode;
  label: string;
  tooltip?: string;
}

const MetricItem = ({ value, label, tooltip }: MetricItemProps) => {
  const content = (
    <div className="bg-gray-50 p-1.5 rounded text-center">
      <div className="text-xs font-medium text-brand-blue truncate">{value}</div>
      <div className="text-[10px] text-gray-500">{label}</div>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {content}
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[200px]">
          <p className="truncate">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
};

interface MarketplaceCardProps {
  /** Main title content */
  title: ReactNode;
  /** Optional secondary title or badge */
  titleExtra?: ReactNode;
  /** Optional subtitle or posting info */
  subtitle?: ReactNode;
  /** Main description or notes, can be truncated */
  description?: string;
  /** Key metrics to display in the card (DR, DA, etc.) */
  metrics?: MetricItemProps[];
  /** Number of columns for metrics grid (2-4) */
  metricsColumns?: 2 | 3 | 4;
  /** Optional footer content with actions */
  footer?: ReactNode;
  /** Bottom info section (e.g., user info or expiry date) */
  bottomInfo?: ReactNode;
  /** Link destination for the entire card */
  linkTo?: string;
  /** Additional className for the card */
  className?: string;
  /** Additional className for the content area */
  contentClassName?: string;
  /** Whether the card should hover effect */
  hoverEffect?: boolean;
  /** Additional content to display */
  children?: ReactNode;
  /** Action label for the default footer button */
  actionLabel?: string;
}

export function MarketplaceCard({
  title,
  titleExtra,
  subtitle,
  description,
  metrics = [],
  metricsColumns = 3,
  footer,
  bottomInfo,
  linkTo,
  className,
  contentClassName,
  hoverEffect = true,
  children,
  actionLabel = "View Details",
}: MarketplaceCardProps) {
  // If no footer is provided but we have a linkTo, create a default footer with "View Details" button
  const renderFooter = footer || (linkTo && (
    <Button size="sm" variant="default" asChild className="w-full">
      <Link to={linkTo}>{actionLabel}</Link>
    </Button>
  ));

  return (
    <TooltipProvider>
      <Card 
        className={cn(
          "h-full flex flex-col",
          hoverEffect && "hover:shadow-md transition-shadow",
          className
        )}
      >
        <CardContent className={cn("p-3 pt-3 pb-3 flex-grow flex flex-col", contentClassName)}>
          <div className="flex justify-between items-start gap-2 mb-2">
            <div className="flex-1">
              {typeof title === 'string' ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-sm font-semibold line-clamp-2 min-h-[2.5rem]">
                      {title}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-[200px]">
                    <p className="truncate font-medium">{title}</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div className="min-h-[2.5rem]">{title}</div>
              )}
              
              {subtitle && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {subtitle}
                </div>
              )}
            </div>
            
            {titleExtra && (
              <div className="flex-shrink-0">
                {titleExtra}
              </div>
            )}
          </div>
          
          {description && (
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-xs text-gray-600 mb-2 line-clamp-1">{description}</p>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-[200px]">
                <p className="truncate">{description}</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {metrics.length > 0 && (
            <div className={cn("grid gap-1.5 mb-2", 
              metrics.length <= 2 ? `grid-cols-${metrics.length}` : 
              `grid-cols-2 md:grid-cols-${metricsColumns}`
            )}>
              {metrics.map((metric, index) => (
                <MetricItem 
                  key={index} 
                  value={metric.value} 
                  label={metric.label} 
                  tooltip={metric.tooltip} 
                />
              ))}
            </div>
          )}

          {children}

          {bottomInfo && (
            <div className="mt-auto w-full overflow-hidden">
              {bottomInfo}
            </div>
          )}
        </CardContent>
        
        {renderFooter && (
          <CardFooter className="p-2 pt-2 border-t">
            {renderFooter}
          </CardFooter>
        )}
      </Card>
    </TooltipProvider>
  );
} 